import React from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {PATHS} from 'Routes';
import documents from '../../../utils/docTypes';
import {valueof} from '../../../utils/types';
import {Statuses, useErrorModal} from 'utils/hooks';
import {useMRZDetection, useDocumentSelfieDetection} from '../../../hooks';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import {useIdentityVerification} from '../useIdentityVerification';
import {TIMEOUT_BEFORE_REDIRECT_MS} from 'utils/constants';
import {
  LocationStates,
  IdentityLocationStateType,
} from 'views/VerifyModalScreen/VerifyModalScreen';
import {ConfirmIdentityTemplate} from 'components/ConfirmIdentity';

type FrontSideDocumentIdentityProps = {
  documentType: valueof<typeof documents.types>;
  nationality: string;
};

const FrontSideDocumentIdentity = React.memo(
  ({documentType, nationality}: FrontSideDocumentIdentityProps) => {
    const history = useHistory();
    const {t} = useTranslation();
    const location = useLocation<IdentityLocationStateType>();
    const {displayError, ErrorModal} = useErrorModal();
    const timeoutRef = React.useRef<any>();
    const reservationDetails = useComputedReservationDetails();
    const {isDocTwoSides, isNeedCheckMrz} = useIdentityVerification({
      nationality,
      documentType,
    });
    const preloadedPhoto =
      location.state?.[LocationStates.frontSideDocumentPhoto]?.picture;

    const {
      startMRZDetection,
      displayMrzError,
      status: statusMrzDetection,
      MrzErrorModal,
    } = useMRZDetection();
    const {
      startDocumentFaceDetection,
      status: statusBySelfieDection,
      DetectionErrorModal,
      resetDetection,
      error,
    } = useDocumentSelfieDetection();

    const status =
      statusMrzDetection !== Statuses.idle ? statusMrzDetection : statusBySelfieDection;

    const getPersistedState = React.useCallback(
      (
        image: string,
        data?: Record<string, any>,
        isPassed?: boolean,
      ): IdentityLocationStateType => {
        return {
          ...location.state,
          [LocationStates.frontSideDocumentPhoto]: {
            checkId: data?.id,
            picture: image,
          },
          [LocationStates.identityVerificationState]: {
            documentsPassed: isPassed,
            isIdentityVerificationFinished: typeof isPassed !== 'undefined',
          },
        };
      },
      [location.state],
    );

    const handleSuccessDocumentFaceDetection = React.useCallback(
      (image: string, data?: any) => {
        timeoutRef.current = setTimeout(() => {
          if (isDocTwoSides) {
            history.push(PATHS.verificationDocumentBack, getPersistedState(image, data));
          } else {
            history.push(PATHS.verificationSelfie, getPersistedState(image, data));
          }
          resetDetection();
        }, TIMEOUT_BEFORE_REDIRECT_MS);
      },
      [getPersistedState, history, isDocTwoSides, resetDetection],
    );

    const handleSuccessMrzDetection = React.useCallback(
      (image: string, data: any) => {
        const state = getPersistedState(image, data, true);
        return history.push(PATHS.verificationResult, {
          ...state,
          isVerifiedOrRetake: true,
        });
      },
      [getPersistedState, history],
    );

    const handleErrorMrzDetection = React.useCallback(
      (image: string) => {
        if (reservationDetails.isVerifyOnlyDocumentOptional) {
          const state = getPersistedState(image, {}, false);

          return history.push(PATHS.verificationResult, {
            ...state,
            isVerifiedOrRetake: true,
          });
        }
        displayMrzError();
      },
      [
        displayMrzError,
        getPersistedState,
        history,
        reservationDetails.isVerifyOnlyDocumentOptional,
      ],
    );

    React.useEffect(() => {
      return () => {
        clearTimeout(timeoutRef.current);
      };
    }, []);

    const handleSendDocumentFaceDetection = React.useCallback(
      async (image: string, reservationId: string) => {
        try {
          await startDocumentFaceDetection({
            image,
            reservationId,
            onSuccess: handleSuccessDocumentFaceDetection,
          });
        } catch (error) {
          displayError(error);
        }
      },
      [displayError, handleSuccessDocumentFaceDetection, startDocumentFaceDetection],
    );

    const handleMrzDetection = React.useCallback(
      async (image: string, reservationId: string) => {
        const needCheckCurrentSide = !isDocTwoSides && isNeedCheckMrz;
        if (!needCheckCurrentSide) {
          history.push(PATHS.verificationDocumentBack, getPersistedState(image));
        }

        try {
          await startMRZDetection({
            image,
            reservationId,
            onSuccess: handleSuccessMrzDetection,
            onError: () => handleErrorMrzDetection(image),
          });
        } catch (error) {
          displayError(error);
        }
      },
      [
        displayError,
        getPersistedState,
        handleErrorMrzDetection,
        handleSuccessMrzDetection,
        history,
        isDocTwoSides,
        isNeedCheckMrz,
        startMRZDetection,
      ],
    );

  const sendScreenshot = React.useCallback(
      (screenshot: string) => {
        const reservationId = reservationDetails?.reservation?.id;
        const needCheckMrz = reservationDetails.hasScanDocument && isNeedCheckMrz;

        const needCheckBiomatchBySelfie = reservationDetails.hasBiomatch;
        if (needCheckMrz) {
          handleMrzDetection(screenshot, reservationId);
          return;
        }
        if (needCheckBiomatchBySelfie) {
          handleSendDocumentFaceDetection(screenshot, reservationId);
          return;
        }

        handleSuccessMrzDetection(screenshot, {});
      },
      [
        handleMrzDetection,
        handleSendDocumentFaceDetection,
        handleSuccessMrzDetection,
        isNeedCheckMrz,
        reservationDetails.hasBiomatch,
        reservationDetails.hasScanDocument,
        reservationDetails?.reservation?.id,
      ],
    );

    // const enableScreenshotButton = () => {
    //   if (isMounted.current) {
    //     setIsCaptureButtonDisabled(false);
    //   }
    // };
    //
    //@ts-ignore
    // const isFileUpload = location?.state?.identityType === IdentityVerificationMethod.file;

    // const onRetry = () => {
    //   setScreenshot('');
    //   const newLocalstate: PhotoLocationStateType = {
    //     ...localLocationState,
    //     documentPhoto: '',
    //   };
    //   const newState = {...location.state, [stateName]: newLocalstate};
    //   history.replace(location.pathname, newState);
    // };
    //
    // const handleErrorMrzDetection = (image?: string) => {
    //   if (reservationDetails.isVerifyOnlyDocumentOptional) {
    //     return redirectAfterTimeout(image);
    //   }
    //   // displayMrzError();
    // };

    return (
      <>
        <ConfirmIdentityTemplate
          status={status}
          error={error}
          onSubmit={sendScreenshot}
          preloadedScreenshot={preloadedPhoto}
          detailsText={t('first_we_take_front_photo')}
          stateName={LocationStates.frontSideDocumentPhoto}
        />
        <DetectionErrorModal />
        <MrzErrorModal />
        <ErrorModal />
      </>
    );
  },
);

export {FrontSideDocumentIdentity};
